import { useEffect } from "react";
import { Helmet } from "react-helmet";
import AIheader from "../RentAI/AIheader";
import { HomeAgentic } from "../Home/components/HomeAgentic";
import { HomeProductTrio } from "../Home/components/HomeProductTrio";
import { SpeakCta, ASSESSMENT_URL } from "../Home/components/SpeakCta";
import { TntHero } from "./components/TntHero";
import { TntBenefits } from "./components/TntBenefits";
import { TntRentDirectBand } from "./components/TntRentDirectBand";
import { RentShieldInsurance } from "./components/RentShieldInsurance";
import { RentAcademyBlock } from "./components/RentAcademyBlock";
import { TntBookingCta } from "./components/TntBookingCta";
import { SimpleFooter } from "./components/SimpleFooter";
import "../Home/home.css";
import "./tnt.css";

const TITLE = "TNT Group — Turo Host Tips & Tricks Partner | Rentid";
const DESCRIPTION =
  "An exclusive Rentid partnership for the Turo Host Tips & Tricks (TNT Group) community. Unlock member-only savings on the most modern direct rental platform — branded booking, renter verification, embedded insurance, and fleet management.";
const OG_DESCRIPTION =
  "Turo Host Tips & Tricks members get more with Rentid. Exclusive member benefits on the most modern direct rental platform.";
const URL = "https://www.rentid.io/tnt-group";

/* Product card backgrounds matched to the home page cards. */
const RENTDIRECT_GRADIENT = "radial-gradient(120% 100% at 30% 20%, #20242C 0%, #111318 60%, #0A0B0E 100%)";
const RENTSHIELD_GRADIENT = "radial-gradient(115% 95% at 90% 88%, #FF5C7A 0%, #C42463 28%, #5E1C48 54%, #1C0B18 80%, #050505 100%)";
const RENTACADEMY_GRADIENT = "radial-gradient(115% 95% at 90% 88%, #FFB020 0%, #C25812 32%, #3C2308 66%, #050505 100%)";

/* Below the benefits section the page is solid black: the light theme tokens flip so the shared
   blocks render legible light text (v0 overrides --background/--foreground/--muted-foreground). */
const DARK_PAGE_VARS = {
  "--tw-c-background": "0 0 0",
  "--tw-c-foreground": "255 255 255",
  "--tw-c-muted-foreground": "164 164 164",
};

/* /tnt-group, ported from the v0 prototype's app/tnt-group/page.tsx (origin/v0/update-hero-sub-copy,
   3cc2c29, 2026-10-13): the TNT Group (Turo Host Tips & Tricks) partner landing page. A light hero
   and the member-benefits section, then the product lineup on black reusing the home page's
   rentID and rentAI/rentSuccess sections, with "Start for Free" CTAs that all go to
   /transition-plan. Rendered inside `.rd-home.rd-tnt` so the scoped Tailwind build and home.css's
   reset apply; tnt.css carries the light palette and the page's own animations. The shared site
   header sits above; v0's minimal footer closes the page. */
export default function TntGroup() {
  // Pages on this site reset scroll themselves (scrollRestoration is manual).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={OG_DESCRIPTION} />
        <meta property="og:url" content={URL} />
        <link rel="canonical" href={URL} />
      </Helmet>
      {/* Shared site header; the wrapper gives it the home page's rounded bottom corners (rd-site.css)
          while it stays black over the light hero. */}
      <div className="rd-rounded-header">
        <AIheader />
      </div>
      <div className="rd-home rd-tnt">
        <main className="tnt-main min-h-screen bg-background text-foreground overflow-x-hidden">
          <TntHero />
          <TntBenefits />

          <div className="bg-black" style={DARK_PAGE_VARS}>
            {/* Product ecosystem — order matches the suite logo lineup above */}
            <div id="product-rentdirect" className="scroll-mt-24 overflow-x-clip">
              <TntRentDirectBand gradient={RENTDIRECT_GRADIENT} />
            </div>
            <div id="product-rentid" className="scroll-mt-24 pt-10 md:pt-12">
              <HomeAgentic hideLearnMore wide spacious logoClassName="h-[54px] sm:h-[68px]" ctaLabel="Start for Free" />
            </div>
            <div id="product-rentshield" className="scroll-mt-24" style={{ "--tw-c-muted": "0 0 0" }}>
              <RentShieldInsurance
                logo="/logos/products/rentshield-inshur-white.svg"
                gradient={RENTSHIELD_GRADIENT}
                offRentPrice="$37"
                glassPills
                darkBadgeText
                logoClassName="h-[43px] sm:h-[54px]"
                plainLogo="/logos/products/rentshield-white-trim.png"
                plainLogoClassName="h-[43px] sm:h-[54px]"
              />
            </div>
            <div id="product-rentai" className="scroll-mt-24">
              <HomeProductTrio sections={["ai"]} hideLearnMore matchLogos />
              <div className="relative flex justify-center -mt-28 pb-20">
                <SpeakCta href={ASSESSMENT_URL} label="Start for Free" />
              </div>
            </div>
            <div id="product-rentsuccess" className="scroll-mt-24">
              <HomeProductTrio sections={["success", "features"]} hideLearnMore wide matchLogos />
            </div>
            <div className="flex justify-center pt-10 pb-6">
              <SpeakCta href={ASSESSMENT_URL} label="Start for Free" />
            </div>
            <div id="product-rentacademy" className="scroll-mt-24">
              <RentAcademyBlock logo="/logos/products/rentacademy-white.png" gradient={RENTACADEMY_GRADIENT} logoClassName="h-[54px] sm:h-[68px]" />
            </div>

            <TntBookingCta />
            <SimpleFooter />
          </div>
        </main>
      </div>
    </>
  );
}
